import type { OperationColumnFilters } from './types';

// Forme minimale d'une ligne d'opération nécessaire au filtrage.
// Compatible avec les OperationRow de SharedAccount et PersonalBudget.
export interface FilterableRow {
  operationType: 'credit' | 'debit';
  categoryId: string;
  subcategoryId: string;
  customLabel: string;
  amount: string;
  operationDate: string; // YYYY-MM-DD
  splitType?: string;
  isRecurring: boolean;
  isValidated: boolean;
  includeInCoupleIncome?: boolean;
  isInternalTransfer?: boolean;
  isSharedExpense?: boolean;
  isNew?: boolean;
}

type NameResolver = (
  categoryId: string,
  operationType: 'credit' | 'debit'
) => string;

type SubNameResolver = (
  categoryId: string,
  subcategoryId: string,
  operationType: 'credit' | 'debit'
) => string;

// Teste si une ligne (existante) passe les filtres actifs.
function rowMatches(
  row: FilterableRow,
  filters: OperationColumnFilters,
  getCategoryName: NameResolver,
  getSubcategoryName: SubNameResolver
): boolean {
  // --- Date ---
  if (filters.date.start && row.operationDate < filters.date.start) return false;
  if (filters.date.end && row.operationDate > filters.date.end) return false;

  // --- Description ---
  const { categoryId, subcategoryId, text } = filters.description;
  if (categoryId && row.categoryId !== categoryId) return false;
  if (subcategoryId && row.subcategoryId !== subcategoryId) return false;
  const search = text.trim().toLowerCase();
  if (search) {
    const haystack = [
      getCategoryName(row.categoryId, row.operationType),
      getSubcategoryName(row.categoryId, row.subcategoryId, row.operationType),
      row.customLabel || '',
    ]
      .join(' ')
      .toLowerCase();
    if (!haystack.includes(search)) return false;
  }

  // --- Montant ---
  const amt = parseFloat(row.amount);
  const min = filters.amount.min ? parseFloat(filters.amount.min) : null;
  const max = filters.amount.max ? parseFloat(filters.amount.max) : null;
  if (min !== null && !isNaN(min) && (isNaN(amt) || amt < min)) return false;
  if (max !== null && !isNaN(max) && (isNaN(amt) || amt > max)) return false;
  if (filters.amount.types.length && !filters.amount.types.includes(row.operationType)) {
    return false;
  }

  // --- Statut ---
  const { validation, recurrence, flags, splitTypes } = filters.status;
  if (validation.length) {
    const v = row.isValidated ? 'validated' : 'unvalidated';
    if (!validation.includes(v)) return false;
  }
  if (recurrence.length) {
    const r = row.isRecurring ? 'recurring' : 'oneoff';
    if (!recurrence.includes(r)) return false;
  }
  // Chaque drapeau coché doit être vrai sur la ligne (ET).
  for (const flag of flags) {
    if (flag === 'internalTransfer' && !row.isInternalTransfer) return false;
    if (flag === 'coupleIncome' && !row.includeInCoupleIncome) return false;
    if (flag === 'sharedExpense' && !row.isSharedExpense) return false;
  }
  if (splitTypes.length) {
    if (!row.splitType || !splitTypes.includes(row.splitType as never)) return false;
  }

  return true;
}

// Applique les filtres aux lignes. Les lignes nouvelles/brouillon (isNew)
// sont toujours conservées ; seuls les filtres des colonnes actives s'appliquent
// aux lignes existantes.
export function applyColumnFilters<T extends FilterableRow>(
  rows: T[],
  filters: OperationColumnFilters,
  getCategoryName: NameResolver,
  getSubcategoryName: SubNameResolver
): T[] {
  return rows.filter(
    (row) => row.isNew || rowMatches(row, filters, getCategoryName, getSubcategoryName)
  );
}
